import { __decorate, __param } from 'vscode/external/tslib/tslib.es6.js';
import './outlinePane.css.js';
import { $, append, Dimension } from 'vscode/vscode/vs/base/browser/dom';
import { ProgressBar } from 'vscode/vscode/vs/base/browser/ui/progressbar/progressbar';
import { TimeoutTimer, timeout } from 'vscode/vscode/vs/base/common/async';
import { DisposableStore, MutableDisposable, toDisposable } from 'vscode/vscode/vs/base/common/lifecycle';
import { LRUCache } from 'vscode/vscode/vs/base/common/map';
import { localizeWithPath } from 'vscode/vscode/vs/nls';
import { IConfigurationService } from 'vscode/vscode/vs/platform/configuration/common/configuration.service';
import { IContextKeyService } from 'vscode/vscode/vs/platform/contextkey/common/contextkey.service';
import { IContextMenuService } from 'vscode/vscode/vs/platform/contextview/browser/contextView.service';
import { IInstantiationService } from 'vscode/vscode/vs/platform/instantiation/common/instantiation';
import { IKeybindingService } from 'vscode/vscode/vs/platform/keybinding/common/keybinding.service';
import { WorkbenchDataTree } from 'vscode/vscode/vs/platform/list/browser/listService';
import { IStorageService } from 'vscode/vscode/vs/platform/storage/common/storage.service';
import { IThemeService } from 'vscode/vscode/vs/platform/theme/common/themeService.service';
import { ViewPane } from 'vscode/vscode/vs/workbench/browser/parts/views/viewPane';
import { IEditorService } from 'vscode/vscode/vs/workbench/services/editor/common/editorService.service';
import { basename } from 'vscode/vscode/vs/base/common/resources';
import { IViewDescriptorService } from 'vscode/vscode/vs/workbench/common/views.service';
import { IOpenerService } from 'vscode/vscode/vs/platform/opener/common/opener.service';
import { ITelemetryService } from 'vscode/vscode/vs/platform/telemetry/common/telemetry.service';
import { OutlineViewState } from './outlineViewState.js';
import { IOutlineService } from 'vscode/vscode/vs/workbench/services/outline/browser/outline.service';
import { EditorResourceAccessor } from 'vscode/vscode/vs/workbench/common/editor';
import { CancellationTokenSource } from 'vscode/vscode/vs/base/common/cancellation';
import { Event } from 'vscode/vscode/vs/base/common/event';
import { TreeFindMode, AbstractTreeViewState } from 'vscode/vscode/vs/base/browser/ui/tree/abstractTree';
import { ctxFollowsCursor, ctxFilterOnType, ctxSortMode, ctxAllCollapsed } from 'vscode/vscode/vs/workbench/contrib/outline/browser/outline';
import { defaultProgressBarStyles } from 'vscode/vscode/vs/platform/theme/browser/defaultStyles';
import { IHoverService } from 'vscode/vscode/vs/platform/hover/browser/hover.service';

const _moduleId = "vs/workbench/contrib/outline/browser/outlinePane";
class OutlineTreeSorter {
    constructor(_comparator, order) {
        this._comparator = _comparator;
        this.order = order;
    }
    compare(a, b) {
        if (this.order === 2 ) {
            return this._comparator.compareByType(a, b);
        }
        else if (this.order === 1 ) {
            return this._comparator.compareByName(a, b);
        }
        else {
            return this._comparator.compareByPosition(a, b);
        }
    }
}
let OutlinePane = class OutlinePane extends ViewPane {
    static { this.Id = 'outline'; }
    constructor(options, _outlineService, _instantiationService, viewDescriptorService, _storageService, _editorService, configurationService, keybindingService, contextKeyService, contextMenuService, openerService, themeService, telemetryService, hoverService) {
        super(options, keybindingService, contextMenuService, configurationService, contextKeyService, viewDescriptorService, _instantiationService, openerService, themeService, telemetryService, hoverService);
        this._outlineService = _outlineService;
        this._instantiationService = _instantiationService;
        this._storageService = _storageService;
        this._editorService = _editorService;
        this._disposables = ( (new DisposableStore()));
        this._editorControlDisposables = ( (new DisposableStore()));
        this._editorPaneDisposables = ( (new DisposableStore()));
        this._outlineViewState = ( (new OutlineViewState()));
        this._editorListener = ( (new MutableDisposable()));
        this._treeStates = ( (new LRUCache(10)));
        this._outlineViewState.restore(this._storageService);
        this._disposables.add(this._outlineViewState);
        contextKeyService.bufferChangeEvents(() => {
            this._ctxFollowsCursor = ctxFollowsCursor.bindTo(contextKeyService);
            this._ctxFilterOnType = ctxFilterOnType.bindTo(contextKeyService);
            this._ctxSortMode = ctxSortMode.bindTo(contextKeyService);
            this._ctxAllCollapsed = ctxAllCollapsed.bindTo(contextKeyService);
        });
        const updateContext = () => {
            this._ctxFollowsCursor.set(this._outlineViewState.followCursor);
            this._ctxFilterOnType.set(this._outlineViewState.filterOnType);
            this._ctxSortMode.set(this._outlineViewState.sortBy);
        };
        updateContext();
        this._disposables.add(this._outlineViewState.onDidChange(updateContext));
    }
    dispose() {
        this._disposables.dispose();
        this._editorPaneDisposables.dispose();
        this._editorControlDisposables.dispose();
        this._editorListener.dispose();
        super.dispose();
    }
    focus() {
        super.focus();
        this._tree?.domFocus();
    }
    renderBody(container) {
        super.renderBody(container);
        this._domNode = container;
        container.classList.add('outline-pane');
        const progressContainer = $('.outline-progress');
        this._message = $('.outline-message');
        this._progressBar = ( (new ProgressBar(progressContainer, defaultProgressBarStyles)));
        this._treeContainer = $('.outline-tree');
        append(container, progressContainer, this._message, this._treeContainer);
        this._disposables.add(this.onDidChangeBodyVisibility(visible => {
            if (!visible) {
                this._editorListener.clear();
                this._editorPaneDisposables.clear();
                this._editorControlDisposables.clear();
            }
            else if (!this._editorListener.value) {
                const event = Event.any(this._editorService.onDidActiveEditorChange, this._outlineService.onDidChange);
                this._editorListener.value = event(() => this._handleEditorChanged(this._editorService.activeEditorPane));
                this._handleEditorChanged(this._editorService.activeEditorPane);
            }
        }));
    }
    layoutBody(height, width) {
        super.layoutBody(height, width);
        this._tree?.layout(height, width);
        this._treeDimensions = new Dimension(width, height);
    }
    collapseAll() {
        this._tree?.collapseAll();
    }
    expandAll() {
        this._tree?.expandAll();
    }
    get outlineViewState() {
        return this._outlineViewState;
    }
    _showMessage(message) {
        this._domNode.classList.add('message');
        this._progressBar.stop().hide();
        this._message.innerText = message;
    }
    _captureViewState(uri) {
        if (this._tree) {
            const oldOutline = this._tree.getInput();
            if (!uri) {
                uri = oldOutline?.uri;
            }
            if (oldOutline && uri) {
                this._treeStates.set(`${oldOutline.outlineKind}/${uri}`, this._tree.getViewState());
                return true;
            }
        }
        return false;
    }
    _handleEditorChanged(pane) {
        this._editorPaneDisposables.clear();
        if (pane) {
            this._editorPaneDisposables.add(pane.onDidChangeControl(() => {
                this._handleEditorControlChanged(pane);
            }));
        }
        this._handleEditorControlChanged(pane);
    }
    async _handleEditorControlChanged(pane) {
        const resource = EditorResourceAccessor.getOriginalUri(pane?.input);
        const didCapture = this._captureViewState();
        this._editorControlDisposables.clear();
        if (!pane || !this._outlineService.canCreateOutline(pane) || !resource) {
            return this._showMessage(( localizeWithPath(_moduleId, 0, "The active editor cannot provide outline information.")));
        }
        let loadingMessage;
        if (!didCapture) {
            loadingMessage = ( (new TimeoutTimer(() => {
                this._showMessage(( localizeWithPath(_moduleId, 1, "Loading document symbols for '{0}'...", basename(resource))));
            }, 100)));
        }
        this._progressBar.infinite().show(500);
        const cts = ( (new CancellationTokenSource()));
        this._editorControlDisposables.add(toDisposable(() => cts.dispose(true)));
        const newOutline = await this._outlineService.createOutline(pane, 1 , cts.token);
        loadingMessage?.dispose();
        if (!newOutline) {
            return;
        }
        if (cts.token.isCancellationRequested) {
            newOutline?.dispose();
            return;
        }
        this._editorControlDisposables.add(newOutline);
        this._progressBar.stop().hide();
        const sorter = ( (new OutlineTreeSorter(newOutline.config.comparator, this._outlineViewState.sortBy)));
        const tree = this._instantiationService.createInstance(WorkbenchDataTree, 'OutlinePane', this._treeContainer, newOutline.config.delegate, newOutline.config.renderers, newOutline.config.treeDataSource, {
            ...newOutline.config.options,
            sorter,
            expandOnDoubleClick: false,
            expandOnlyOnTwistieClick: true,
            multipleSelectionSupport: false,
            hideTwistiesOfChildlessElements: true,
            defaultFindMode: this._outlineViewState.filterOnType ? TreeFindMode.Filter : TreeFindMode.Highlight,
            overrideStyles: this.getLocationBasedColors().listOverrideStyles
        });
        const updateTree = () => {
            if (newOutline.isEmpty) {
                this._showMessage(( localizeWithPath(_moduleId, 2, "No symbols found in document '{0}'", basename(resource))));
                this._captureViewState(resource);
                tree.setInput(undefined);
            }
            else if (!tree.getInput()) {
                this._domNode.classList.remove('message');
                const state = this._treeStates.get(`${newOutline.outlineKind}/${newOutline.uri}`);
                tree.setInput(newOutline, state && AbstractTreeViewState.lift(state));
            }
            else {
                this._domNode.classList.remove('message');
                tree.updateChildren();
            }
        };
        updateTree();
        this._editorControlDisposables.add(newOutline.onDidChange(updateTree));
        tree.findMode = this._outlineViewState.filterOnType ? TreeFindMode.Filter : TreeFindMode.Highlight;
        this._editorControlDisposables.add(this.viewDescriptorService.onDidChangeLocation(({ views }) => {
            if (( (views.some(v => v.id === this.id)))) {
                tree.updateOptions({ overrideStyles: this.getLocationBasedColors().listOverrideStyles });
            }
        }));
        this._editorControlDisposables.add(tree.onDidChangeFindMode(mode => this._outlineViewState.filterOnType = mode === TreeFindMode.Filter));
        let idPool = 0;
        this._editorControlDisposables.add(tree.onDidOpen(async (e) => {
            const myId = ++idPool;
            const isDoubleClick = e.browserEvent?.type === 'dblclick';
            if (!isDoubleClick) {
                await timeout(150);
                if (myId !== idPool) {
                    return;
                }
            }
            await newOutline.reveal(e.element, e.editorOptions, e.sideBySide, isDoubleClick);
        }));
        const revealActiveElement = () => {
            if (!this._outlineViewState.followCursor || !newOutline.activeElement) {
                return;
            }
            let item = newOutline.activeElement;
            while (item) {
                const top = tree.getRelativeTop(item);
                if (top === null) {
                    tree.reveal(item, 0.5);
                }
                if (tree.getRelativeTop(item) !== null) {
                    tree.setFocus([item]);
                    tree.setSelection([item]);
                    break;
                }
                item = tree.getParentElement(item);
            }
        };
        revealActiveElement();
        this._editorControlDisposables.add(newOutline.onDidChange(revealActiveElement));
        this._editorControlDisposables.add(this._outlineViewState.onDidChange((e) => {
            this._outlineViewState.persist(this._storageService);
            if (e.filterOnType) {
                tree.findMode = this._outlineViewState.filterOnType ? TreeFindMode.Filter : TreeFindMode.Highlight;
            }
            if (e.followCursor) {
                revealActiveElement();
            }
            if (e.sortBy) {
                sorter.order = this._outlineViewState.sortBy;
                tree.resort();
            }
        }));
        let viewState;
        this._editorControlDisposables.add(tree.onDidChangeFindPattern(pattern => {
            if (tree.findMode === TreeFindMode.Highlight) {
                return;
            }
            if (!viewState && pattern) {
                viewState = tree.getViewState();
                tree.expandAll();
            }
            else if (!pattern && viewState) {
                tree.setInput(tree.getInput(), viewState);
                viewState = undefined;
            }
        }));
        const updateAllCollapsedCtx = () => {
            this._ctxAllCollapsed.set(tree.getNode(null).children.every(node => !node.collapsible || node.collapsed));
        };
        this._editorControlDisposables.add(tree.onDidChangeCollapseState(updateAllCollapsedCtx));
        this._editorControlDisposables.add(tree.onDidChangeModel(updateAllCollapsedCtx));
        updateAllCollapsedCtx();
        tree.layout(this._treeDimensions?.height, this._treeDimensions?.width);
        this._tree = tree;
        this._editorControlDisposables.add(toDisposable(() => {
            tree.dispose();
            this._tree = undefined;
        }));
    }
};
OutlinePane = ( (__decorate([
    ( (__param(1, IOutlineService))),
    ( (__param(2, IInstantiationService))),
    ( (__param(3, IViewDescriptorService))),
    ( (__param(4, IStorageService))),
    ( (__param(5, IEditorService))),
    ( (__param(6, IConfigurationService))),
    ( (__param(7, IKeybindingService))),
    ( (__param(8, IContextKeyService))),
    ( (__param(9, IContextMenuService))),
    ( (__param(10, IOpenerService))),
    ( (__param(11, IThemeService))),
    ( (__param(12, ITelemetryService))),
    ( (__param(13, IHoverService)))
], OutlinePane)));

export { OutlinePane };
